/**
 * Адаптер «картина сезона»: серверный ответ → формы прототипа.
 *
 * Зачем адаптер, а не правка экранов. Кабинет решений Astra считал сводку в
 * браузере (`astra/model.ts: summarize`) по журналу `analysis.json`: он
 * разворачивал все обязательства района и сворачивал их обратно. На
 * канонических объёмах (сорок с лишним тысяч обязательств в одном цикле) это
 * запрещено — экран получает ГОТОВЫЕ агрегаты сервера. Но сами экраны при
 * этом не переписываются: здесь серверный ответ переводится в те же формы
 * (`Summary`, `WorkSummary`, недельный ряд, строки по массивам), которые
 * кабинет читал раньше.
 *
 * Правило перевода одно и оно жёсткое: ЧЕГО СЕРВЕР НЕ ДАЁТ — ТО `null`, а не
 * ноль. Ноль на этих экранах читается как «посчитали и получилось нисколько»;
 * отсутствие поля означает «не спрашивали» или «сервер не называет». Разница
 * видна человеку словами на экране, а не прячется в одинаковую цифру.
 */

import { api, type QueryParams, type SeasonPicture, type Schemas } from '../../../api/client';
import type { DatasetAdapter } from './adapter';
import { toWork } from './works';
import type { Cycle, CycleStatus } from '../../data/types';
import type { Journal, Scope, Summary, WorkSummary } from '../../astra/model';
import { round } from '../../astra/model';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  massifNo: {
    id: 'data.season.massifNo',
    defaultMessage: 'Массив №{id}',
    description: 'Название массива в таблице территорий, когда сервер имя не назвал; {id} — номер массива',
  },
});


type SeasonCycles = Schemas['SeasonCyclesOut'];
type SeasonCycleRow = Schemas['SeasonCycleOut'];
type Measured = Schemas['MeasuredOut'];

/** Имя набора, под которым канонический источник показывается человеку. */
// i18n-ignore: метка трека данных (аналог «A»/«B»), а не фраза интерфейса
export const CANON_TRACK = 'КАНОН';

/* -- единицы ------------------------------------------------------------- */

/** Микрогектары сервера → гектары экрана. `null` остаётся `null`. */
export const hectares = (microha: number | null | undefined): number | null =>
  microha === null || microha === undefined ? null : round(microha / 1_000_000);

/**
 * Измеренная величина сервера → число экрана.
 *
 * `value === null` означает «посчитать было не из чего», и подставить сюда
 * ноль значило бы выдать отсутствие корпуса за измеренный нуль.
 */
export const measured = (row: Measured | null | undefined): number | null =>
  row === null || row === undefined ? null : row.value;

/** Объём воды приезжает строкой (десятичная точность). `null` — нет записей. */
export const volume = (raw: string | null | undefined): number | null => {
  if (raw === null || raw === undefined) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? round(parsed) : null;
};

/* -- параметры запроса --------------------------------------------------- */

/**
 * Срез прототипа → параметры сервера.
 *
 * `cycleId` прототипа на каноне — это ключ цикла `"<сезон>:<КУЛЬТУРА>"` из
 * `/season/cycles`; массив и хозяйство — числовые идентификаторы канона,
 * приехавшие теми же выдачами. Нечисловой идентификатор (остаток фикстурного
 * адреса) в запрос НЕ уходит: сервер ответил бы на вопрос, которого никто не
 * задавал.
 */
export function seasonQuery(scope: Scope, extra: QueryParams = {}): QueryParams {
  // Демо едет ФЛАГОМ СРЕЗА (галка «Показывать демо-данные»), а не зашитой
  // истиной: раньше здесь стояло `include_demo: true`, и кабинет решений
  // показывал синтетику при выключенной галке, а карточки хозяйства и массива
  // на том же срезе — нет. Флаг не задан — параметр не уходит, и сервер
  // отвечает по умолчанию (демо выключено).
  const params: QueryParams = { as_of: scope.asOf };
  if (scope.includeDemo) params.include_demo = true;
  if (scope.cycleId.includes(':')) params.cycle = scope.cycleId;
  const massif = Number(scope.massifId);
  if (scope.massifId && Number.isInteger(massif)) params.massif_id = massif;
  const actor = Number(scope.farmId);
  if (scope.farmId && Number.isInteger(actor)) params.actor_id = actor;
  return { ...params, ...extra };
}

/* -- циклы --------------------------------------------------------------- */

const CYCLE_STATUS: Record<string, CycleStatus> = {
  planned: 'planned',
  active: 'active',
  closed: 'closed',
};

/**
 * Культура цикла в словаре каркаса. Канон зовёт озимую пшеницу «Ғалла»
 * (`GRAIN`), каркас и переключатель культуры — `wheat`; остальные коды едут
 * строчными, как и раньше.
 */
const CROP_OF_CODE: Record<string, string> = { GRAIN: 'wheat' };

export function cropOfCode(code: string): string {
  return CROP_OF_CODE[code] ?? code.toLowerCase();
}

/** Ключ цикла сервера — он же идентификатор цикла для среза прототипа. */
export function toCycle(row: SeasonCycleRow, horizon: string): Cycle {
  const total = row.obligations.total;
  return {
    id: row.cycle,
    // Шаблона агроцикла у районного цикла нет: цикл собирается из кампаний.
    template_id: '',
    crop: cropOfCode(row.crop_code),
    season_label: row.season_label,
    status: CYCLE_STATUS[row.status] ?? 'planned',
    window: { from: row.window_from ?? horizon, to: row.window_to ?? horizon },
    farms: row.actors.total,
    contours: row.contours.total,
    area_ha: hectares(row.area.total_microha) ?? 0,
    stage_now: row.stage?.name ?? null,
    progress: {
      works_total: row.campaigns,
      works_done: 0,
      works_active: 0,
      reported_pct: total > 0 ? round((100 * row.obligations.reported) / total) : 0,
      verified_pct: total > 0 ? round((100 * row.obligations.verified) / total) : 0,
    },
  };
}

/**
 * Оболочка журнала для каркаса кабинета.
 *
 * Журнала на каноне НЕТ и подделывать его нечем: обязательства, водные строки
 * и урожай на сервере не выгружаются списком в браузер, и именно поэтому мы
 * сюда и переезжаем. Здесь заполнено ровно то, что каркас спрашивает у набора
 * (какие циклы есть, до какой даты собраны данные, как называется набор), а
 * списки оставлены пустыми намеренно: читать их на каноническом маршруте
 * нельзя, и экраны, которым они нужны, спрашивают источник (`sourceKind`).
 */
export function toJournalShell(cycles: SeasonCycles): Journal {
  return {
    version: cycles.context.rules_version ?? '',
    track: CANON_TRACK,
    as_of: cycles.horizon,
    cycles: cycles.items.map((row) => toCycle(row, cycles.horizon)),
    works: [],
    obligations: [],
    water: [],
    results: [],
    evidence: 'demo',
  };
}

/* -- работы цикла -------------------------------------------------------- */

/**
 * Одна работа сводки — ТЕМ ЖЕ переводом, что и на маршруте работ цикла
 * (`works.ts: toWork`). Двух переводов одного ответа быть не должно: как
 * только у работы появляются две формы, у неё появляются и два разных
 * идентификатора, и ссылка «Сейчас в поле» ведёт на работу, которой на
 * экране работы нет. Идентификатор работы — номер кампании канона, строкой.
 */

export function toWorkSummaries(picture: SeasonPicture, scope: Scope): WorkSummary[] {
  const works = picture.summary?.works ?? [];
  return works.map((row, index) => {
    const total = row.totals.obligations.total;
    const area = hectares(row.area.total_microha);
    const reportedArea = hectares(row.area.reported_microha);
    return {
      work: toWork(row, scope.cycleId, index + 1, picture.horizon),
      total,
      reported: row.totals.obligations.reported,
      inProgress: row.totals.obligations.in_progress ?? null,
      verified: row.totals.obligations.verified,
      // Правило сводки держится и на уровне работы: не измерено — `null`, а не
      // ноль. Ноль на карточке работы читался бы как «после срока никого нет».
      overdue: measured(row.totals.overdue),
      conflicts: measured(row.totals.conflicts),
      area: area ?? 0,
      // Остаток площади — разность двух измеренных величин. Если хотя бы одна
      // не измерена, разности нет: ноль здесь читался бы как «не осталось».
      remainingArea: area === null || reportedArea === null ? null : round(area - reportedArea),
      remainingContours: row.remaining_contours.ids.map(String),
    };
  });
}

/* -- сводка -------------------------------------------------------------- */

/** Строка по массиву — форма `territoryRows` прототипа. */
export interface TerritoryRow {
  id: string;
  name: string;
  total: number;
  reported: number;
  verified: number;
  overdue: number;
  farms: number | null;
}

/** Точка недельного ряда — форма `weeklyFlow` прототипа. */
export interface WeeklyPoint {
  date: string;
  reported: number;
  verified: number;
}

export function toTerritoryRows(picture: SeasonPicture): TerritoryRow[] {
  return (picture.summary?.by_massif ?? []).map((row) => ({
    id: String(row.id),
    name: row.name ?? tr(m.massifNo, { id: row.id }),
    total: row.totals.obligations.total,
    reported: row.totals.obligations.reported,
    verified: row.totals.obligations.verified,
    overdue: measured(row.totals.overdue) ?? 0,
    // Сколько хозяйств стоит за массивом, ответ не называет: у строки массива
    // есть контуры и площадь, но не число землепользователей.
    farms: null,
  }));
}

/**
 * Недельный ряд сервера — это события КАЖДОЙ недели, а не накопленный итог.
 * Накопление с начала сезона по девяти неделям окна не восстанавливается, и
 * режим «Накоплено» на каноне поэтому недоступен (см. `FlowChart`).
 */
export function toWeekly(picture: SeasonPicture): WeeklyPoint[] {
  return (picture.summary?.weekly ?? []).map((row) => ({
    date: row.week_to,
    reported: measured(row.reported) ?? 0,
    verified: measured(row.verified) ?? 0,
  }));
}

export function toWaterRows(picture: SeasonPicture) {
  return (picture.summary?.water_by_actor ?? []).map((row) => ({
    farmId: String(row.actor_id),
    requested: volume(row.requested_volume_m3) ?? 0,
    delivered: volume(row.delivered_volume_m3) ?? 0,
  }));
}

/**
 * `SeasonPicture` → `Summary` прототипа.
 *
 * `rows`/`eligible` остаются пустыми НАМЕРЕННО: это списки обязательств, и
 * именно их выгрузку в браузер переезд отменяет. Экран, которому нужен
 * поимённый список, спрашивает свой эндпоинт, а не разворачивает сводку.
 */
export function toSummary(picture: SeasonPicture, scope: Scope): Summary {
  const water = picture.water;
  const requested = volume(water.requested_volume_m3);
  const delivered = volume(water.delivered_volume_m3);
  return {
    rows: [],
    eligible: [],
    works: toWorkSummaries(picture, scope),
    total: picture.obligations.total,
    reported: picture.obligations.reported,
    inProgress: picture.obligations.in_progress ?? null,
    verified: picture.obligations.verified,
    overdue: measured(picture.overdue),
    conflicts: measured(picture.conflicts),
    area: hectares(picture.area.total_microha) ?? 0,
    affectedArea: hectares(measured(picture.overdue_area)),
    // Сколько контуров стоит за просрочкой, ответ не называет: у площади есть
    // число строк, но строка — не контур, и выдавать одно за другое нельзя.
    affectedContours: null,
    farms: picture.actors.total,
    waterRequested: requested,
    waterDelivered: delivered,
    waterGap: requested === null || delivered === null ? null : round(requested - delivered),
    waterRows: toWaterRows(picture),
    scope,
    definitionVersion: picture.context.rules_version ?? '',
    track: CANON_TRACK,
  };
}

/** Оговорки водного баланса — словарные статьи ответа, а не текст экрана. */
export const waterNotes = (picture: SeasonPicture): string[] =>
  picture.water_notes.map((note) => note.text);

/**
 * Наборы среза, которые умеет отдать канонический источник.
 *
 * Регистрируются в `data-source/api.ts` одной строкой. Имя набора — вопрос
 * экрана, а не адрес эндпоинта: экран, спрашивающий картину сезона, не должен
 * знать, как она называется у источника.
 */
const SEASON_SETS: Record<string, (params: QueryParams) => Promise<unknown>> = {
  'season.picture.json': (params: QueryParams) => api.seasonPicture({ ...params, summary: true }),
  'season.cycles.json': (params: QueryParams) => api.seasonCycles(params),
};

export const seasonAdapter: DatasetAdapter = {
  domain: 'season',
  match: (name) => name in SEASON_SETS,
  load: (name, params) => {
    const load = SEASON_SETS[name];
    // Сюда попадают только имена, на которые адаптер сам согласился в `match`;
    // отказ оставлен на случай рассинхронизации списка и проверки имени.
    if (!load) // i18n-ignore: ошибка для разработчика
    throw new Error(`набор «${name}» не входит в картину сезона`);
    return load(params);
  },
};
